<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CopyBillingListItems',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    sources: {
      type: Array,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getItem = (...args) => {
    const [sources, fields] = args;
    const fieldNames = _.map(fields, 'name');

    let result = _.reduce(fieldNames, (m, v) => _.set(m, v, null), {});
    result['$updates'] = _.reduce(fieldNames, (m, v) => _.set(m, v, false), {});

    return result;
  };
  const getPayload = (...args) => {
    const [sources, item] = args;

    const updates = _.intersection(
      _.keys(_.pickBy(item.$updates, _.identity)),
      getVisibleFields()
    );

    if (!updates.length) {
      return;
    }

    return { ..._.pick(item, updates), sources };
  };
  const onProductSelected = (...args) => {
    const [data] = args;

    if (!data.name) {
      return;
    }

    item.value.description = data.name;
    item.value.$updates['description'] = true;

    item.value.unit = data.unit;
    item.value.$updates['unit'] = true;
  };
  const getVisibleFields = (...args) => {
    const selectedMode = selectedMode.value;
    return _.chain(fields.value)
      .filter(
        (field) =>
          !_.isArray(field.mode) || field.mode.indexOf(selectedMode) > -1
      )
      .map('name')
      .value();
  };
  const isFieldVisible = (...args) => {
    const [name] = args;
    const mode = _.get(fieldsByName.value[name], 'mode');

    if (_.isArray(mode)) {
      return mode.indexOf(selectedMode.value) > -1;
    }

    return true;
  };

  const isReadonly = ref(false);
  const formModes = ref([
    {
      value: 'discount',
      name: $fn.value.pgettext('CopyBillingListItems', 'Simple discount'),
    },
    {
      value: 'complex',
      name: $fn.value.pgettext('CopyBillingListItems', 'Complex calculations'),
    },
  ]);
  const selectedMode = ref('discount');
  const fields = ref([
    {
      name: 'product',
      help: $fn.value.pgettext(
        'CopyBillingListItems',
        'Override the product or service reference for this line.'
      ),
    },
    {
      name: 'description',
      help: $fn.value.pgettext(
        'CopyBillingListItems',
        'Override the line description with any freeform text.'
      ),
    },
    {
      name: 'intermediated_service',
      help: $fn.value.pgettext(
        'CopyBillingListItems',
        'Set or override whether this line represents an intermediated service.'
      ),
    },
    {
      name: 'unit',
      help: $fn.value.pgettext(
        'CopyBillingListItems',
        'The unit to use to generate the new items.'
      ),
      mode: ['discount', 'complex'],
    },
    {
      name: 'quantity',
      help: $fn.value.pgettext(
        'CopyBillingListItems',
        'Define a fixed quantity to use when generating the new items.'
      ),
      mode: ['complex'],
    },
    {
      name: 'quantity_percentage',
      help: 'P_("CopyBillingListItems", "Specify the quantity as a percentage of the original item’s quantity.")',
      mode: ['complex'],
    },
    {
      name: 'price',
      help: $fn.value.pgettext(
        'CopyBillingListItems',
        'Set a fixed price to apply to the new items, overriding the original price.'
      ),
      mode: ['complex'],
    },
    {
      name: 'price_percentage',
      help: 'P_("CopyBillingListItems", "Set the price as a percentage of the original item’s price.")',
      mode: ['complex'],
    },
    {
      name: 'discount_percentage',
      help: $fn.value.pgettext(
        'CopyBillingListItems',
        'Apply a discount percentage to the new items.'
      ),
      mode: ['discount'],
    },
    {
      name: 'notes',
      help: $fn.value.pgettext(
        'CopyBillingListItems',
        'Add or override the internal notes for the generated invoice line.'
      ),
    },
  ]);
  const fieldsByName = ref(null);
  const labelAlign = ref('right');
  const formId = ref(null);
  const formName = ref('CopyBillingListItems');
  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_814138253067422_dataSource = computed(() => ({
    name: 'suggest_product',
  }));

  const $data = computed(() => ({
    isReadonly: isReadonly.value,
    formModes: formModes.value,
    selectedMode: selectedMode.value,
    fields: fields.value,
    fieldsByName: fieldsByName.value,
    labelAlign: labelAlign.value,
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_814138253067422_dataSource: b_814138253067422_dataSource.value,
  }));

  const $$load_fieldsByName = async () => {
    return _.keyBy($data.value.fields, 'name');
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return getItem(props.sources, $data.value.fields);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'isReadonly' },
      { name: 'formModes' },
      {
        name: 'selectedMode',
        cache: { type: 'local', key: 'CopyBillingListItems.selectedMode' },
      },
      { name: 'fields', dependsOn: ['item'] },
      { name: 'fieldsByName' },
      { name: 'labelAlign' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_814138253067422_dataSource', nodeId: '814138253067422' },
    ],
    loaders: {
      fieldsByName: $$load_fieldsByName,
      formId: $$load_formId,
      item: $$load_item,
    },
    ux: {},
    dataRefs: {
      isReadonly,
      formModes,
      selectedMode,
      fields,
      fieldsByName,
      labelAlign,
      formId,
      formName,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_814138253067422_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'CopyBillingListItems|Title',
        'Copy Billing List Items'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.sources,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('sources');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_qoanwe4lg_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_65jvjg4p6_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_request_kajucqbih_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'copy_billing_list_items',
      data: getPayload(props.sources, item.value),
    };
  };
  const act_814138253067422_script_iem46zfxy_value_callable = async (
    $event
  ) => {
    return onProductSelected($event.data.option);
  };
  const act_814138253067422_script_iem46zfxy_getActionArgs = async ($event) => {
    return {
      value: async () => {
        await act_814138253067422_script_iem46zfxy_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_qoanwe4lg =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_qoanwe4lg_getActionArgs,
    });
  const inst_evh_7315092382398562_close_w984znvcr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_qoanwe4lg,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_w984znvcr],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_kqg0uj949 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_kqcwj63sk =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_kqg0uj949,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_kqcwj63sk],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_fw4xjze3f =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_ukpci0eb5 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_fw4xjze3f,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_ukpci0eb5],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_65jvjg4p6 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_65jvjg4p6_getActionArgs,
    });
  const inst_evh_1040167445267876_success_t4ynv0nex =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_65jvjg4p6,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_request_kajucqbih = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_kajucqbih_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_t4ynv0nex],
  });
  const inst_evh_1040167445267876_submit_k4cimp2p1 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_kajucqbih,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_k4cimp2p1],
    event: 'submit',
  });
  const act_814138253067422_script_iem46zfxy = new actionTypes.ScriptAction({
    actionArgs: act_814138253067422_script_iem46zfxy_getActionArgs,
  });
  const inst_evh_814138253067422_optionSelected_i81mu7vfy =
    new actionTypes.EventHandlerImpl({
      action: act_814138253067422_script_iem46zfxy,
      event: 'optionSelected',
      displayName: 'script',
    });
  const inst_evh_814138253067422_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_814138253067422_optionSelected_i81mu7vfy],
      event: 'optionSelected',
    });
  const act_2434514588044777_closeComponent_3ivdvsmeb =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_qhdrc2dzo =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_3ivdvsmeb,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_qhdrc2dzo],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_w984znvcr,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_kqcwj63sk,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_ukpci0eb5,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_t4ynv0nex,
    inst_evh_1040167445267876_submit_k4cimp2p1,
    save: inst_evh_1040167445267876_submit_k4cimp2p1,
    inst_evh_1040167445267876_submit,
    inst_evh_814138253067422_optionSelected_i81mu7vfy,
    inst_evh_814138253067422_optionSelected,
    inst_evh_2434514588044777_clickPreventStop_qhdrc2dzo,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_w984znvcr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_w984znvcr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_kqcwj63sk = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_kqcwj63sk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_ukpci0eb5 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_ukpci0eb5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_t4ynv0nex = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_t4ynv0nex.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_k4cimp2p1 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_k4cimp2p1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_814138253067422_optionSelected_i81mu7vfy = async (event, scope) => {
    return await actions.inst_evh_814138253067422_optionSelected_i81mu7vfy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_814138253067422_optionSelected = async (event, scope) => {
    return await actions.inst_evh_814138253067422_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_qhdrc2dzo = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_qhdrc2dzo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CopyBillingListItems"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        autofocus="first"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="formMode"
          :label="pgettext('CopyBillingListItems', 'Mode')"
          :label-cols-md="3"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <BFormRadio
            :key="mode.vaue"
            v-model="selectedMode"
            :value="mode.value"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (selectedMode =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            name="formMode"
            v-for="mode in formModes"
          >
            {{ mode.name }}
          </BFormRadio>
        </ItpFormGroup>
        <ItpFormGroup
          name="product"
          :label="pgettext('CopyBillingListItems', 'Service')"
          :label-cols-md="3"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group  d-flex flex-row"
          :helpTooltip="fieldsByName['product'].help"
          v-if="isFieldVisible('product')"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="product_enabled"
              v-model="item.$updates.product"
              :value="item.$updates.product"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.product =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormSelect2
            name="product"
            v-model="item.product"
            :value="item.product"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.product =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            bind-object
            :dataSource="b_814138253067422_dataSource"
            textField="name"
            :disabled="!item.$updates.product"
            class="flex-grow-1"
            @optionSelected="evh_814138253067422_optionSelected($event, {})"
          >
          </ItpFormSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="description"
          :label="pgettext('Invoice', 'Description')"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
          contentClass="input-group"
          :helpTooltip="fieldsByName['description'].help"
          class="col"
          v-if="isFieldVisible('description')"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="description_enabled"
              v-model="item.$updates.description"
              :value="item.$updates.description"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.description =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormInput
            name="description"
            v-model="item.description"
            :value="item.description"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.description =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.description"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="intermediated_service"
          :label="pgettext('Invoice', 'Intermediated service')"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
          contentClass="input-group"
          :helpTooltip="fieldsByName['intermediated_service'].help"
          class="col"
          v-if="isFieldVisible('intermediated_service')"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="intermediated_service_enabled"
              v-model="item.$updates.intermediated_service"
              :value="item.$updates.intermediated_service"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.intermediated_service =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <div class="input-group-text">
            <ItpFormCheckbox
              name="intermediated_service"
              v-model="item.intermediated_service"
              :value="item.intermediated_service"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.intermediated_service =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :disabled="!item.$updates.intermediated_service"
            >
            </ItpFormCheckbox>
          </div>
        </ItpFormGroup>
        <ItpFormGroup
          name="quantity"
          :label="pgettext('Invoice', 'Quantity')"
          :label-cols-md="3"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
          :helpTooltip="fieldsByName['quantity'].help"
          class="col"
          v-if="isFieldVisible('quantity')"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="quantity_enabled"
              v-model="item.$updates.quantity"
              :value="item.$updates.quantity"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.quantity =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormInput
            mask="number"
            unmask="typed"
            :scale="4"
            name="quantity"
            v-model="item.quantity"
            :value="item.quantity"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.quantity =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.quantity"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="quantity_percentage"
          :label="pgettext('Invoice', 'Quantity %')"
          :label-cols-md="3"
          :content-cols-md="3"
          :labelAlign="labelAlign"
          contentClass="input-group"
          :helpTooltip="fieldsByName['quantity_percentage'].help"
          appendText="%"
          class="col"
          v-if="isFieldVisible('quantity_percentage')"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="quantity_percentage_enabled"
              v-model="item.$updates.quantity_percentage"
              :value="item.$updates.quantity_percentage"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.quantity_percentage =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormInput
            mask="number"
            unmask="typed"
            :scale="4"
            name="quantity_percentage"
            v-model="item.quantity_percentage"
            :value="item.quantity_percentage"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.quantity_percentage =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.quantity_percentage"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="unit"
          :label="pgettext('Invoice', 'Unit')"
          :label-cols-md="3"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
          :helpTooltip="fieldsByName['unit'].help"
          class="col"
          v-if="isFieldVisible('unit')"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="unit_enabled"
              v-model="item.$updates.unit"
              :value="item.$updates.unit"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.unit =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormInput
            name="unit"
            v-model="item.unit"
            :value="item.unit"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.unit =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.unit"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="price"
          :label="pgettext('Invoice', 'Price')"
          :label-cols-md="3"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
          :helpTooltip="fieldsByName['price'].help"
          class="col"
          v-if="isFieldVisible('price')"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="price_enabled"
              v-model="item.$updates.price"
              :value="item.$updates.price"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.price =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormInput
            mask="number"
            unmask="typed"
            :scale="2"
            name="price"
            v-model="item.price"
            :value="item.price"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.price =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.price"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="price_percentage"
          :label="pgettext('Invoice', 'Price %')"
          :label-cols-md="3"
          :content-cols-md="3"
          :labelAlign="labelAlign"
          contentClass="input-group"
          :helpTooltip="fieldsByName['price_percentage'].help"
          appendText="%"
          class="col"
          v-if="isFieldVisible('price_percentage')"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="price_percentage_enabled"
              v-model="item.$updates.price_percentage"
              :value="item.$updates.price_percentage"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.price_percentage =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormInput
            mask="number"
            unmask="typed"
            :scale="4"
            name="price_percentage"
            v-model="item.price_percentage"
            :value="item.price_percentage"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.price_percentage =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.price_percentage"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="discount_percentage"
          :label="pgettext('Invoice', 'Discount %')"
          :label-cols-md="3"
          :content-cols-md="3"
          :labelAlign="labelAlign"
          contentClass="input-group"
          :helpTooltip="fieldsByName['discount_percentage'].help"
          appendText="%"
          class="col"
          v-if="isFieldVisible('discount_percentage')"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="discount_percentage_enabled"
              v-model="item.$updates.discount_percentage"
              :value="item.$updates.discount_percentage"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.discount_percentage =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormInput
            mask="number"
            unmask="typed"
            :scale="4"
            name="discount_percentage"
            v-model="item.discount_percentage"
            :value="item.discount_percentage"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.discount_percentage =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.discount_percentage"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="notes"
          :label="pgettext('Invoice', 'Notes')"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
          contentClass="input-group"
          :helpTooltip="fieldsByName['notes'].help"
          class="col"
          v-if="isFieldVisible('notes')"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="notes_enabled"
              v-model="item.$updates.notes"
              :value="item.$updates.notes"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.notes =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormTextarea
            name="notes"
            v-model="item.notes"
            :value="item.notes"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.notes =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.notes"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms[formName] || !forms[formName].submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
